import { GuestBot } from "../interfaces"

/** Интервал поллинга списка созданий (спека §3.2). */
export const PROVISION_POLL_MS = 30000

export const isProvisionTerminal = (
  status: GuestBot.ProvisionStatus,
): boolean => status === "completed" || status === "failed"

/**
 * Активная запись: создание идёт. Застрявший `draft` с `lastError`
 * (500 при запуске цепочки; retry/cancel нет) активным НЕ считается —
 * единственный предикат активности для поллинга/счётчиков/видимости (спека §2).
 */
export const isProvisionActive = (
  status: GuestBot.ProvisionStatus,
  lastError?: string | null,
): boolean => status === "provisioning" || (status === "draft" && !lastError)

/** Неудача для UI: терминальный `failed` либо застрявший `draft` с `lastError`. */
export const isProvisionFailedForUi = (
  status: GuestBot.ProvisionStatus,
  lastError?: string | null,
): boolean => status === "failed" || (status === "draft" && !!lastError)

/** Человекочитаемые тексты чипов (спека §1.5). */
const ERROR_TEXT: Record<string, string> = {
  "auth-failed": "Не удалось войти в BotFather",
  "username-taken": "Юзернейм занят",
  "invalid-username": "Неверный юзернейм",
  "api-changed": "BotFather изменился",
  "http-error": "Ошибка сети",
  "no-bots-access": "Нет доступа к созданию ботов",
  "provision stage finished with no bot": "Процесс завершился без бота",
}

const ERROR_HINT: Record<string, string> = {
  "auth-failed": "Повторите позже; проверьте доступность webview",
  "username-taken": "Возьмите другой юзернейм и создайте заново",
  "invalid-username": "5–32 символа: латиница/цифры/_, на конце «bot»",
  "api-changed": "Сообщите разработчикам",
  "http-error": "Повторите запрос",
  "no-bots-access": "Проверьте аккаунт-исполнитель роли",
  "provision stage finished with no bot":
    "Повторите создание с другим юзернеймом",
}

export const getProvisionErrorText = (
  lastError: string | null | undefined,
  message?: string,
): string => {
  if (lastError) return ERROR_TEXT[lastError] ?? lastError
  return message ?? "Ошибка создания"
}

export const getProvisionErrorTooltip = (
  lastError: string | null | undefined,
  adopted?: boolean,
): string => {
  const hint = lastError ? (ERROR_HINT[lastError] ?? lastError) : ""
  const adoptedNote = "подключён существующий бот"
  if (hint && adopted) return `${hint}. ${adoptedNote}`
  if (adopted) return adoptedNote
  return hint
}

/**
 * Локальный чип уместен только когда серверной записи гарантированно нет:
 * 400/404 отклоняют запрос до создания записи, FETCH_ERROR — сеть.
 * 500/409 оставляют запись (`draft` c lastError / уже запущен) — чип придёт списком.
 */
export const isProvisionLocalError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false
  const status = (error as { status?: unknown }).status
  return status === 400 || status === 404 || status === "FETCH_ERROR"
}

/** Требование Telegram: 5..32 символа [A-Za-z0-9_], суффикс bot (регистронезависимо). */
export const isValidBotUsername = (username: string): boolean =>
  /^[a-z0-9_]{2,29}bot$/i.test(username)
